import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onCatalogOptionClick"],
  _excluded2 = ["resultMLOptionId"];
import { useMemo } from 'react';
import { NavMarker } from 'react-rhumb';
import { View } from 'foundations-components/components/View';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import { Section } from 'foundations-components/transitional/components/Section';
import ApiError from '../../../components/Errors/ApiError';
import NoResultsError from '../../../components/Errors/NoResultsError';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useDisplayedData } from '../../table';
import { PAGE_SIZE } from '../../pagination';
import { LearningContentCard } from 'academy-common-lib/features/learningContentCard/components/LearningContentCard';
import { useCatalogSearchResults, useSearchPlaceholder } from '../searchHooks';
import { useSearchFeedback } from '../searchQueries';
import Raven from 'raven-js';
import { useSearchState } from '../searchStore';
import { scrollPageToTop } from 'academy-common-lib/utils/scrolling';
import { FiltersCard } from '../../filters/components/FiltersCard';
import { CertificationsFilter } from '../../filters/components/CertificationsFilter';
import { DurationFilter } from '../../filters/components/DurationFilter';
import { AllContentTypesFilter } from '../../filters/components/AllContentTypesFilter';
import { LevelFilter } from '../../filters/components/LevelFilter';
import { LanguageFilter } from '../../filters/components/LanguageFilter';
import { PageResultsCount } from '../../header/components/PageResultsCount';
// @ts-expect-error Could not find a declaration file for module
import { useSearchParam } from '../../../hooks/searchParamHooks';
import { FILTER_TYPES } from '../../../constants/TopFilterConstants';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { DropdownContentSourceFilter } from '../../filters/components/DropdownContentSourceFilter';
import { usePageTitle } from '../../../hooks/usePageTitle';
import I18n from 'I18n';
import { Flex } from 'foundations-components/components/Flex';
import { ACCatalogMultiSelect } from 'academy-navigation-lib/components/ACCatalog/ACCatalogMultiSelect';
import ResultsStatusBar from '../../../components/ResultsStatusBar';
import { SEARCH_FILTERS } from '../searchConsts';
import { ACSearchInput } from 'academy-navigation-lib/components/ACSearchInput/ACSearchInput';
import { useNavPageHandlers } from '../../navigation';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SEARCH_RESULTS_SIZE = PAGE_SIZE;
const getCatalogTitleI18n = (search, category, tools) => {
  if (search) {
    return {
      key: 'learningCenter.dynamicPageTitles.catalogSearch',
      options: {
        searchTerm: search
      }
    };
  }
  if (category) {
    const categoryKey = `learningCenter.topFilters.cat.${category}`;
    const categoryName = I18n.text(categoryKey);
    return {
      key: 'learningCenter.dynamicPageTitles.catalogCategory',
      options: {
        categoryName: categoryName && categoryName !== categoryKey ? categoryName : category
      }
    };
  }
  if (tools) {
    const toolKey = `learningCenter.topFilters.tools.${tools}`;
    const toolName = I18n.text(toolKey);
    return {
      key: 'learningCenter.dynamicPageTitles.catalogTool',
      options: {
        toolName: toolName && toolName !== toolKey ? toolName : tools
      }
    };
  }
  return {};
};
export const CatalogSearchResults = ({
  banners,
  isPartnerPage,
  pageUrlFragment
}) => {
  const {
    results,
    loading,
    error,
    totalCount,
    page,
    setPage,
    searchCriteria,
    correlationId
  } = useCatalogSearchResults({
    partnersOnly: isPartnerPage,
    pageSize: SEARCH_RESULTS_SIZE
  });
  const [sendSearchFeedback] = useSearchFeedback();
  const {
    setLastClickedSearchResultItem
  } = useSearchState();
  const [[category]] = useSearchParam(FILTER_TYPES.CATEGORY);
  const [[tools]] = useSearchParam(FILTER_TYPES.TOOLS);
  const {
    key: catalogTitleKey,
    options: catalogTitleOptions
  } = useMemo(() => getCatalogTitleI18n(searchCriteria === null || searchCriteria === void 0 ? void 0 : searchCriteria.search, category, tools), [searchCriteria === null || searchCriteria === void 0 ? void 0 : searchCriteria.search, category, tools]);
  usePageTitle({
    i18nKey: catalogTitleKey,
    options: catalogTitleOptions,
    skip: !catalogTitleKey
  });
  const {
    results: displayedSearchResults,
    totalCount: displayedTotalCount = 0,
    searchCriteria: displayedSearchCriteria,
    category: displayedCategory,
    tools: displayedTools
  } = useDisplayedData({
    results,
    totalCount,
    searchCriteria,
    category,
    tools
  }, {
    loading,
    parameters: {
      page,
      searchCriteria
    }
  }) || {};
  const {
    isMobile,
    isTablet
  } = useMediaQuery();
  const _useNavPageHandlers = useNavPageHandlers({
      appearsIn: 'catalog-search-results'
    }),
    {
      onCatalogOptionClick
    } = _useNavPageHandlers,
    searchInputHandlers = _objectWithoutPropertiesLoose(_useNavPageHandlers, _excluded);
  const searchPlaceholder = useSearchPlaceholder();
  const handlePageChange = event => {
    setPage(event.target.value);
    scrollPageToTop();
  };
  const handleOnItemClick = (_ref, position) => {
    let {
        resultMLOptionId
      } = _ref,
      item = _objectWithoutPropertiesLoose(_ref, _excluded2);
    setLastClickedSearchResultItem({
      item: {
        id: item.id,
        type: item.type,
        language: item.language
      },
      position
    });
    if (!correlationId) return;
    sendSearchFeedback({
      variables: {
        correlationId,
        contentContainerType: item.type,
        resultMLOptionId
      }
    }).catch(Raven.captureException.bind(Raven));
  };
  const resultsContent = /*#__PURE__*/_jsx(_Fragment, {
    children: !displayedSearchResults && loading ? /*#__PURE__*/_jsx(LoadingSpinner, {
      layout: "centered",
      minHeight: 250,
      size: "medium"
    }) : error ? /*#__PURE__*/_jsx(ApiError, {
      error: error,
      marker404: "CATALOG_COURSES_API_404",
      markerDefault: "CATALOG_COURSES_API_ERROR",
      resetLink: pageUrlFragment
    }) : !(displayedSearchResults !== null && displayedSearchResults !== void 0 && displayedSearchResults.length) && !loading ? /*#__PURE__*/_jsx(NavMarker, {
      name: "CATALOG_COURSES_LOADED",
      children: /*#__PURE__*/_jsx(NoResultsError, {
        resetLink: pageUrlFragment
      })
    }) : /*#__PURE__*/_jsx(NavMarker, {
      name: "CATALOG_COURSES_LOADED",
      children: /*#__PURE__*/_jsxs(View, {
        "data-test-id": "main-catalog-cards-container",
        marginBlockStart: isMobile ? '0' : '400',
        children: [/*#__PURE__*/_jsx(Section, {
          children: /*#__PURE__*/_jsx(CardGrid, {
            gap: isMobile ? '200' : '600',
            gridTemplateColumns: isTablet ? '1fr' : 'repeat(auto-fill, minmax(296px, 1fr))',
            children: displayedSearchResults === null || displayedSearchResults === void 0 ? void 0 : displayedSearchResults.map((item, index) => /*#__PURE__*/_jsx(LearningContentCard, {
              extraTrackingInfo: {
                source: 'searchV2'
              },
              item: item,
              onItemClick: () => handleOnItemClick(item, index + 1),
              overviewType: "filterable-results",
              position: index + 1,
              size: isTablet ? 'small' : 'large'
            }, `item_${item.id}`))
          })
        }), /*#__PURE__*/_jsx(Section, {
          children: /*#__PURE__*/_jsx(Paginator, {
            onPageChange: handlePageChange,
            page: page,
            pageCount: Math.ceil(totalCount / SEARCH_RESULTS_SIZE),
            showFirstLastButtons: true
          })
        })]
      })
    })
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [banners, !isMobile && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        flexDirection: "row",
        gap: "400",
        inlineSize: "67%",
        marginBlockEnd: "400",
        children: [/*#__PURE__*/_jsx(ACCatalogMultiSelect, {
          onCatalogOptionClick: onCatalogOptionClick
        }), /*#__PURE__*/_jsx(View, {
          blockSize: "42px",
          flexGrow: 1,
          maxInlineSize: "100%",
          minInlineSize: "0",
          position: "relative",
          zIndex: "3",
          children: /*#__PURE__*/_jsx(View, {
            inlineSize: "100%",
            position: "absolute",
            children: /*#__PURE__*/_jsx(ACSearchInput, Object.assign({
              placeholder: searchPlaceholder,
              use: "on-dark"
            }, searchInputHandlers))
          })
        })]
      }), displayedSearchResults && /*#__PURE__*/_jsx(PageResultsCount, {
        page: page - 1,
        pageSize: SEARCH_RESULTS_SIZE,
        searchCriteria: {
          search: displayedSearchCriteria === null || displayedSearchCriteria === void 0 ? void 0 : displayedSearchCriteria.search,
          category: displayedCategory,
          hubspotSoftware: displayedTools
        },
        total: displayedTotalCount
      }), /*#__PURE__*/_jsxs(FiltersCard, {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.myLearning.filterBy"
        }), /*#__PURE__*/_jsx(CertificationsFilter, {
          appearsIn: "results"
        }), /*#__PURE__*/_jsx(LevelFilter, {
          appearsIn: "results"
        }), /*#__PURE__*/_jsx(DurationFilter, {
          appearsIn: "results"
        }), /*#__PURE__*/_jsx(AllContentTypesFilter, {
          appearsIn: "results"
        }), /*#__PURE__*/_jsx(DropdownContentSourceFilter, {
          appearsIn: "results"
        }), /*#__PURE__*/_jsx(LanguageFilter, {
          appearsIn: "results"
        })]
      })]
    }), isMobile && /*#__PURE__*/_jsx(_Fragment, {
      children: /*#__PURE__*/_jsx(View, {
        borderBlockEndColor: "border-core-subtle",
        borderBlockEndStyle: "solid",
        borderBlockEndWidth: "100",
        marginBlockEnd: "200",
        marginBlockStart: "-400",
        marginInline: "-400",
        children: /*#__PURE__*/_jsx(ResultsStatusBar, {
          cardsTotal: displayedTotalCount,
          endCard: Math.min(page * PAGE_SIZE, displayedTotalCount),
          loading: !displayedSearchResults && loading,
          mobileFilterTypes: SEARCH_FILTERS,
          startCard: (page - 1) * PAGE_SIZE + 1,
          sticky: true
        })
      })
    }), resultsContent]
  });
};